import React, { useState, useEffect } from 'react';
import { FaEdit, FaCheck, FaTimes, FaCamera } from 'react-icons/fa';
import type { FC } from 'react';
import { getHeaders } from '../../utils/api';
import { API_CONFIG } from '../../config/api';

interface EditNewsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (formData: NewsFormData) => void;
  news: {
    id: number;
    mainTopic: string;
    additionalTopicsEn: string | string[];
    additionalTopicsDe: string | string[];
    title: string;
    summary: string;
    imageUrl: string;
    link: string;
    language: string;
    translatedTitle: string | null;
    translatedSummary: string | null;
  } | null;
}

export interface NewsFormData {
  mainTopic: string;
  additionalTopicsEn: string;
  additionalTopicsDe: string;
  title: string;
  summary: string;
  imageUrl: string;
  link: string;
  language: string;
  translatedTitle: string;
  translatedSummary: string;
}

interface ClientNewsDTO {
  id: number;
  mainTopic: string;
  mainTopicImageUrl?: string | null;
  additionalTopics: string[];
  title: string;
  summary: string;
  imageUrl: string;
  link: string;
  authorName?: string | null;
  authorImage?: string | null;
  publishedAt: number;
  countryCode?: string | null;
}

const FaEditIcon = FaEdit as unknown as FC;
const FaCheckIcon = FaCheck as unknown as FC;
const FaTimesIcon = FaTimes as unknown as FC;
const FaCameraIcon = FaCamera as unknown as FC;

const EditNewsModal: React.FC<EditNewsModalProps> = ({
  isOpen,
  onClose,
  onSave,
  news
}) => {
  const [formData, setFormData] = useState<NewsFormData>({
    mainTopic: '',
    additionalTopicsEn: '',
    additionalTopicsDe: '',
    title: '',
    summary: '',
    imageUrl: '',
    link: '',
    language: '',
    translatedTitle: '',
    translatedSummary: ''
  });
  const [editingField, setEditingField] = useState<string | null>(null);
  const [tempValue, setTempValue] = useState<string>('');
  const [tempImage, setTempImage] = useState<string | null>(null);
  const [germanNews, setGermanNews] = useState<ClientNewsDTO | null>(null);
  const [germanLoading, setGermanLoading] = useState(false);
  const [germanError, setGermanError] = useState<string | null>(null);
  const [germanEditingField, setGermanEditingField] = useState<string | null>(null);
  const [germanTempValue, setGermanTempValue] = useState<string>('');
  const [germanTempNews, setGermanTempNews] = useState<ClientNewsDTO | null>(null);

  useEffect(() => {
    if (news) {
      setFormData({
        mainTopic: news.mainTopic || '',
        additionalTopicsEn: Array.isArray(news.additionalTopicsEn) 
          ? news.additionalTopicsEn.join(', ') 
          : news.additionalTopicsEn || '',
        additionalTopicsDe: Array.isArray(news.additionalTopicsDe) 
          ? news.additionalTopicsDe.join(', ') 
          : news.additionalTopicsDe || '',
        title: news.title || '',
        summary: news.summary || '',
        imageUrl: news.imageUrl || '',
        link: news.link || '',
        language: news.language || '',
        translatedTitle: news.translatedTitle || '',
        translatedSummary: news.translatedSummary || ''
      });
      setTempImage(null);
      setEditingField(null);
    }
  }, [news]);

  useEffect(() => {
    if (news && news.id) {
      setGermanLoading(true);
      setGermanError(null);
      fetch(`${API_CONFIG.BASE_URL}/admin/news/translation/${news.id}/country/de/language/de`, {
        headers: getHeaders(),
      })
        .then(res => {
          if (!res.ok) throw new Error('Failed to fetch German translation');
          return res.json();
        })
        .then(data => setGermanNews(data))
        .catch(err => setGermanError('Could not load German translation'))
        .finally(() => setGermanLoading(false));
    }
  }, [news?.id]);

  useEffect(() => {
    if (germanNews) {
      setGermanTempNews(germanNews);
    }
  }, [germanNews]);

  if (!isOpen) return null;

  const handleEdit = (field: string, value: string) => {
    setEditingField(field);
    setTempValue(value);
  };

  const handleEditSave = (field: string) => {
    setFormData(prev => ({ ...prev, [field]: tempValue }));
    setEditingField(null);
  };

  const handleEditCancel = () => {
    setEditingField(null);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = (ev) => {
        setTempImage(ev.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageSave = () => {
    if (tempImage) {
      setFormData(prev => ({ ...prev, imageUrl: tempImage }));
      setTempImage(null);
    }
  };

  const handleGermanEdit = (field: string, value: string) => {
    setGermanEditingField(field);
    setGermanTempValue(value);
  };

  const handleGermanEditSave = async (field: string) => {
    if (!germanTempNews) return;
    const updated = { ...germanTempNews, [field]: germanTempValue };
    setGermanTempNews(updated);
    setGermanEditingField(null);
    // TODO: Replace with your actual update endpoint
    try {
      await fetch(`${API_CONFIG.BASE_URL}/admin/news/translation/${germanTempNews.id}/country/de/language/de`, {
        method: 'POST', // or PATCH/PUT as appropriate
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...updated, id: undefined }), // remove id if backend doesn't expect it
      });
      setGermanNews(updated);
    } catch (e) {
      // Optionally show error
    }
  };

  const handleGermanEditCancel = () => {
    setGermanEditingField(null);
  };

  // Helper for topics as chips
  const renderChips = (topics: string, lang: 'En' | 'De') => {
    const arr = topics.split(',').map(t => t.trim()).filter(Boolean);
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {arr.map((topic, idx) => (
          <span key={idx} style={{ background: '#eee', borderRadius: 12, padding: '2px 8px', marginRight: 4 }}>{topic}</span>
        ))}
        {editingField === `additionalTopics${lang}` && (
          <input
            type="text"
            value={tempValue}
            onChange={e => setTempValue(e.target.value)}
            style={{ marginLeft: 4 }}
          />
        )}
        {editingField === `additionalTopics${lang}` ? (
          <>
            <button type="button" onClick={() => handleEditSave(`additionalTopics${lang}`)}><FaCheckIcon /></button>
            <button type="button" onClick={handleEditCancel}><FaTimesIcon /></button>
          </>
        ) : (
          <button type="button" onClick={() => handleEdit(`additionalTopics${lang}`, formData[`additionalTopics${lang}` as keyof NewsFormData] as string)}><FaEditIcon /></button>
        )}
      </div>
    );
  };

  // Helper for German topics as chips
  const renderGermanChips = (topics: string[], field: string) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {topics.map((topic, idx) => (
        <span key={idx} style={{ background: '#eee', borderRadius: 12, padding: '2px 8px', marginRight: 4 }}>{topic}</span>
      ))}
      {germanEditingField === field && (
        <input
          type="text"
          value={germanTempValue}
          onChange={e => setGermanTempValue(e.target.value)}
          style={{ marginLeft: 4 }}
        />
      )}
      {germanEditingField === field ? (
        <>
          <button type="button" onClick={() => handleGermanEditSave(field)}><FaCheckIcon /></button>
          <button type="button" onClick={handleGermanEditCancel}><FaTimesIcon /></button>
        </>
      ) : (
        <button type="button" onClick={() => handleGermanEdit(field, (germanTempNews?.additionalTopics || []).join(', '))}><FaEditIcon /></button>
      )}
    </div>
  );

  return (
    <div className="modal-overlay">
      <div style={{ display: 'flex', flexDirection: 'row', maxWidth: 900, borderRadius: 18, padding: 0, background: '#fff', margin: '40px auto' }}>
        {/* Left: Editable News */}
        <div className="modal edit-news-modal" style={{ maxWidth: 420, borderRadius: 18, padding: 0, flex: 1, borderRight: '1px solid #eee' }}>
          {/* Image section */}
          <div style={{ position: 'relative', width: '100%', height: 220, overflow: 'hidden', borderTopLeftRadius: 18, borderTopRightRadius: 18 }}>
            <img
              src={tempImage || formData.imageUrl}
              alt="News visual"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            <label style={{ position: 'absolute', bottom: 12, right: 12, background: '#fff', borderRadius: 20, padding: 6, cursor: 'pointer', boxShadow: '0 2px 8px #0002' }}>
              <FaCameraIcon /> Replace Image
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageChange} />
            </label>
            {tempImage && (
              <div style={{ position: 'absolute', top: 12, right: 12 }}>
                <button type="button" onClick={handleImageSave} style={{ marginRight: 8 }}><FaCheckIcon /></button>
                <button type="button" onClick={() => setTempImage(null)}><FaTimesIcon /></button>
              </div>
            )}
          </div>
          <div style={{ padding: 24 }}>
            {/* Title */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 20 }}>
                  {editingField === 'title' ? (
                    <>
                      <input
                        type="text"
                        value={tempValue}
                        onChange={e => setTempValue(e.target.value)}
                        style={{ fontSize: 18, padding: 4 }}
                      />
                      <button type="button" onClick={() => handleEditSave('title')}><FaCheckIcon /></button>
                      <button type="button" onClick={handleEditCancel}><FaTimesIcon /></button>
                    </>
                  ) : (
                    <>
                      {formData.title}
                      <button type="button" onClick={() => handleEdit('title', formData.title)} style={{ marginLeft: 8 }}><FaEditIcon /></button>
                    </>
                  )}
                </span>
              </div>
            </div>
            {/* Summary */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 16 }}>
                  {editingField === 'summary' ? (
                    <>
                      <textarea
                        value={tempValue}
                        onChange={e => setTempValue(e.target.value)}
                        rows={3}
                        style={{ fontSize: 15, padding: 4, width: '100%' }}
                      />
                      <button type="button" onClick={() => handleEditSave('summary')}><FaCheckIcon /></button>
                      <button type="button" onClick={handleEditCancel}><FaTimesIcon /></button>
                    </>
                  ) : (
                    <>
                      {formData.summary}
                      <button type="button" onClick={() => handleEdit('summary', formData.summary)} style={{ marginLeft: 8 }}><FaEditIcon /></button>
                    </>
                  )}
                </span>
              </div>
            </div>
            {/* Topics as chips */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 500, marginBottom: 4 }}>Topics (EN):</div>
              {renderChips(formData.additionalTopicsEn, 'En')}
              <div style={{ fontWeight: 500, margin: '8px 0 4px' }}>Topics (DE):</div>
              {renderChips(formData.additionalTopicsDe, 'De')}
            </div>
            {/* Save/Cancel for all fields */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
              <button type="button" onClick={onClose} style={{ background: '#eee', border: 'none', borderRadius: 6, padding: '8px 18px' }}>Cancel</button>
              <button type="button" onClick={() => onSave(formData)} style={{ background: '#222', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px' }}>Save</button>
            </div>
          </div>
        </div>
        {/* Right: German Translation */}
        <div style={{ flex: 1, padding: 24, minWidth: 0 }}>
          <h3 style={{ marginBottom: 16 }}>German Translation</h3>
          {germanLoading && <div>Loading...</div>}
          {germanError && <div style={{ color: 'red' }}>{germanError}</div>}
          {germanTempNews && (
            <div>
              <div style={{ marginBottom: 16 }}>
                <img src={germanTempNews.imageUrl} alt="German News" style={{ width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 12 }} />
              </div>
              {/* Title */}
              <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 8 }}>
                {germanEditingField === 'title' ? (
                  <>
                    <input
                      type="text"
                      value={germanTempValue}
                      onChange={e => setGermanTempValue(e.target.value)}
                      style={{ fontSize: 16, padding: 4 }}
                    />
                    <button type="button" onClick={() => handleGermanEditSave('title')}><FaCheckIcon /></button>
                    <button type="button" onClick={handleGermanEditCancel}><FaTimesIcon /></button>
                  </>
                ) : (
                  <>
                    {germanTempNews.title}
                    <button type="button" onClick={() => handleGermanEdit('title', germanTempNews.title)} style={{ marginLeft: 8 }}><FaEditIcon /></button>
                  </>
                )}
              </div>
              {/* Summary */}
              <div style={{ fontSize: 15, marginBottom: 8 }}>
                {germanEditingField === 'summary' ? (
                  <>
                    <textarea
                      value={germanTempValue}
                      onChange={e => setGermanTempValue(e.target.value)}
                      rows={3}
                      style={{ fontSize: 15, padding: 4, width: '100%' }}
                    />
                    <button type="button" onClick={() => handleGermanEditSave('summary')}><FaCheckIcon /></button>
                    <button type="button" onClick={handleGermanEditCancel}><FaTimesIcon /></button>
                  </>
                ) : (
                  <>
                    {germanTempNews.summary}
                    <button type="button" onClick={() => handleGermanEdit('summary', germanTempNews.summary)} style={{ marginLeft: 8 }}><FaEditIcon /></button>
                  </>
                )}
              </div>
              {/* Main Topic */}
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontWeight: 500 }}>Main Topic: </span>
                {germanEditingField === 'mainTopic' ? (
                  <>
                    <input
                      type="text"
                      value={germanTempValue}
                      onChange={e => setGermanTempValue(e.target.value)}
                      style={{ fontSize: 15, padding: 4 }}
                    />
                    <button type="button" onClick={() => handleGermanEditSave('mainTopic')}><FaCheckIcon /></button>
                    <button type="button" onClick={handleGermanEditCancel}><FaTimesIcon /></button>
                  </>
                ) : (
                  <>
                    {germanTempNews.mainTopic}
                    <button type="button" onClick={() => handleGermanEdit('mainTopic', germanTempNews.mainTopic)} style={{ marginLeft: 8 }}><FaEditIcon /></button>
                  </>
                )}
              </div>
              {/* Additional Topics */}
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontWeight: 500 }}>Additional Topics: </span>
                {renderGermanChips(germanTempNews.additionalTopics, 'additionalTopics')}
              </div>
              {/* Link, Author, etc. remain read-only */}
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontWeight: 500 }}>Link: </span>
                <a href={germanTempNews.link} target="_blank" rel="noopener noreferrer">{germanTempNews.link}</a>
              </div>
              {germanTempNews.authorName && (
                <div style={{ marginBottom: 8 }}>
                  <span style={{ fontWeight: 500 }}>Author: </span>{germanTempNews.authorName}
                  {germanTempNews.authorImage && <img src={germanTempNews.authorImage} alt="Author" style={{ width: 32, height: 32, borderRadius: '50%', marginLeft: 8, verticalAlign: 'middle' }} />}
                </div>
              )}
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontWeight: 500 }}>Published: </span>{new Date(germanTempNews.publishedAt).toLocaleString()}
              </div>
              {germanTempNews.countryCode && (
                <div style={{ marginBottom: 8 }}>
                  <span style={{ fontWeight: 500 }}>Country Code: </span>{germanTempNews.countryCode}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditNewsModal; 